import { Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { TimerWorkListComponent } from '../components/timer-work-list/timer-work-list.component';
import { combineLatest, firstValueFrom, map } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionStateService } from '../../../services/session-state.service';
import { WorkService } from '../../../services/work.service';
import { switchMap } from 'rxjs/operators';
import { TimerService } from '../../../services/timer.service';
import type { FirestoreWork, IsoDate } from '@jessie/commons';
import {
  WorkSlideOverComponent,
  type WorkSlideOverInput,
} from '../../projects/slide-overs/work-slide-over.component';
import { mapFirestoreTimerToRow } from '../components/timer-work-list/rows/timer-row.component';
import { Dialog } from '@angular/cdk/dialog';
import { CalendarStatusService } from '../components/calendar/calendar/calendar-status.service';

@Component({
  selector: 'app-timer-work-list-page',
  imports: [AsyncPipe, TimerWorkListComponent],
  templateUrl: './timer-work-list.page.html',
})
export class TimerWorkListPage {
  sessionState = inject(SessionStateService);
  workService = inject(WorkService);
  dialog = inject(Dialog);
  timerService = inject(TimerService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  calendarStatus = inject(CalendarStatusService);

  selectedDate = this.activatedRoute.params.pipe(map((params) => params.selectedDate));

  works = combineLatest([this.sessionState.getOrgaUserId(), this.selectedDate]).pipe(
    switchMap(([orgaUserId, selectedDate]) =>
      this.workService.getAllWorkForUser(orgaUserId, selectedDate, selectedDate)
    )
  );

  suggestions = this.sessionState
    .getOrgaUserId()
    .pipe(switchMap((orgaUserId) => this.workService.getWorkSuggestions(orgaUserId)));

  timerRows = this.selectedDate.pipe(
    switchMap((selectedDate) => this.timerService.getTimersByDate(selectedDate)),
    map((timers) => timers.map(mapFirestoreTimerToRow))
  );

  createTimer(date: IsoDate) {
    this.timerService.createTimer(date);
  }

  showTimer(timerId: string) {
    this.router.navigate(['timer', timerId], { relativeTo: this.activatedRoute });
  }

  createWorkEntry(date: IsoDate) {
    this.openWorkSlideOver({ date });
  }

  async selectSuggestion(suggestion: FirestoreWork) {
    const selectedDate = await firstValueFrom(this.selectedDate);
    this.openWorkSlideOver({
      date: selectedDate,
      project: suggestion.data.project,
      position: suggestion.data.position,
      description: suggestion.data.description,
      customer: suggestion.data.customer ?? undefined,
    });
  }

  async start(timerId: string) {
    await this.timerService.startTimer(timerId);
  }

  async stop() {
    await this.timerService.stopTimer();
  }

  editWork(work: FirestoreWork) {
    this.openWorkSlideOver({ work });
  }

  private openWorkSlideOver(data: WorkSlideOverInput): void {
    const dialogRef = this.dialog.open<{ saved: true }, WorkSlideOverInput, WorkSlideOverComponent>(
      WorkSlideOverComponent,
      { data }
    );

    dialogRef.closed.subscribe((result) => {
      if (result && typeof result === 'object' && 'saved' in result) {
        this.calendarStatus.showSaved();
      }
    });
  }
}
